// frontend/src/pages/Profile/index.tsx

import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { apiClient } from '@/api/client'
import { useAuthStore } from '@/store'
import { useToast } from '@/components/common/UI/Toast'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'
import { Select } from '@/components/common/UI/Select'
import { Card } from '@/components/common/UI/Card'
import { Spinner } from '@/components/common/UI/Spinner'
import { Tabs, Tab, TabPanel } from '@/components/common/UI/Tabs'
import { Modal, useModal } from '@/components/common/UI/Modal'
import { ResumeList } from '@/components/resume/ResumeCard'
import { CompanyList } from '@/components/company/CompanyList'
import { formatDate, getInitialsAvatar, generateRandomColor } from '@/utils/helpers'
import { TelegramSettings } from '@/components/telegram/TelegramSettings'

// ============================================
// Схема валидации профиля
// ============================================

const profileSchema = z.object({
  first_name: z.string().min(1, 'Имя обязательно').max(100),
  last_name: z.string().min(1, 'Фамилия обязательна').max(100),
  phone: z.string().regex(/^\+?[0-9]{10,15}$/, 'Введите корректный номер телефона').optional().or(z.literal('')),
  language: z.string().min(2),
})

type ProfileFormData = z.infer<typeof profileSchema>

// ============================================
// Компонент профиля
// ============================================

const Profile: React.FC = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, updateUser, logout } = useAuthStore()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('profile')
  const [isUploading, setIsUploading] = useState(false)
  const passwordModal = useModal()
  const access = useQuery({ queryKey: ['billing','me'], queryFn: () => apiClient.get<any>('/v1/billing/me'), enabled: isAuthenticated && (user?.role === 'candidate' || user?.role === 'employer') })
  const plans = useQuery({ queryKey: ['billing','catalog'], queryFn: () => apiClient.get<any>('/v1/billing/catalog'), enabled: isAuthenticated && (user?.role === 'candidate' || user?.role === 'employer') })

  const avatarColor = generateRandomColor()
  const initials = getInitialsAvatar(
    user?.full_name || user?.first_name || 'Пользователь'
  )

  // Редирект если не авторизован
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, navigate])

  // Форма профиля
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
      language: user?.language || 'ru',
    }
  })

  // Обновление при изменении пользователя
  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        language: user.language || 'ru',
      })
    }
  }, [user, reset])

  // Получение статистики пользователя
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['user', 'stats'],
    queryFn: async () => {
      return apiClient.get('/v1/users/me/stats')
    },
    enabled: !!user
  })

  // Получение резюме пользователя
  const { data: resumesData, isLoading: resumesLoading } = useQuery({
    queryKey: ['user', 'resumes'],
    queryFn: async () => {
      return apiClient.get('/v1/resumes/my', { params: { limit: 10 } })
    },
    enabled: !!user && user?.role === 'candidate'
  })

  // Получение компаний пользователя
  const { isLoading: companiesLoading } = useQuery({
    queryKey: ['user', 'companies'],
    queryFn: async () => {
      return apiClient.get('/v1/companies/my', { params: { limit: 10 } })
    },
    enabled: !!user && (user?.role === 'employer' || user?.role === 'admin')
  })

  // Мутация обновления профиля
  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const response = await apiClient.patch('/v1/users/me', data)
      return response
    },
    onSuccess: (data) => {
      updateUser(data)
      queryClient.invalidateQueries({ queryKey: ['user', 'stats'] })
      showToast('Профиль обновлен', 'success')
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.detail || 'Ошибка обновления профиля',
        'error'
      )
    }
  })

  // Мутация обновления аватара
  const updateAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await apiClient.upload('/v1/users/me/avatar', file)
      return response
    },
    onSuccess: (data) => {
      updateUser({ ...user, avatar_url: data.avatar_url })
      queryClient.invalidateQueries({ queryKey: ['user', 'stats'] })
      showToast('Аватар обновлен', 'success')
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.detail || 'Ошибка загрузки аватара',
        'error'
      )
    }
  })

  // Мутация смены пароля
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { old_password: string; new_password: string }) => {
      await apiClient.post('/v1/users/me/change-password', data)
    },
    onSuccess: () => {
      passwordModal.close()
      showToast('Пароль изменен', 'success')
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.detail || 'Ошибка смены пароля',
        'error'
      )
    }
  })

  // Обработчик отправки формы
  const onSubmit = (data: ProfileFormData) => {
    updateProfileMutation.mutate(data)
  }

  // Обработчик загрузки аватара
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Проверка типа
    if (!file.type.startsWith('image/')) {
      showToast('Файл должен быть изображением', 'error')
      return
    }

    // Проверка размера (макс 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Размер файла не должен превышать 5MB', 'error')
      return
    }

    setIsUploading(true)
    updateAvatarMutation.mutate(file, {
      onSettled: () => setIsUploading(false)
    })
  }

  // Обработчик смены пароля
  const handlePasswordChange = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const oldPassword = (form.elements.namedItem('old_password') as HTMLInputElement).value
    const newPassword = (form.elements.namedItem('new_password') as HTMLInputElement).value
    const confirmPassword = (form.elements.namedItem('confirm_password') as HTMLInputElement).value

    if (newPassword !== confirmPassword) {
      showToast('Пароли не совпадают', 'error')
      return
    }

    if (newPassword.length < 8) {
      showToast('Пароль должен содержать минимум 8 символов', 'error')
      return
    }

    changePasswordMutation.mutate({ old_password: oldPassword, new_password: newPassword })
  }

  if (!user) {
    return (
      <div className="container-custom py-12 flex justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  const isCandidate = user.role === 'candidate'
  const isEmployer = user.role === 'employer' || user.role === 'admin'

  return (
    <div className="container-custom py-8">
      {/* Заголовок */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Личный кабинет</h1>
        <Button variant="ghost" onClick={logout} className="text-error hover:text-error/90">
          Выйти
        </Button>
      </div>

      {/* Профиль */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Левая колонка - аватар и информация */}
        <div className="lg:col-span-1">
          <Card className="p-6 text-center">
            {/* Аватар */}
            <div className="relative inline-block">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name || user.first_name}
                  className="w-32 h-32 rounded-full object-cover mx-auto"
                />
              ) : (
                <div
                  className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold text-white mx-auto"
                  style={{ backgroundColor: avatarColor }}
                >
                  {initials}
                </div>
              )}
              
              {/* Кнопка загрузки аватара */}
              <label className="absolute bottom-0 right-0 p-1 bg-primary-600 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={isUploading}
                />
              </label>
            </div>

            {isUploading && (
              <div className="mt-2 flex justify-center">
                <Spinner size="sm" />
              </div>
            )}

            <h2 className="mt-4 text-lg font-semibold">
              {user.full_name || user.first_name || 'Пользователь'}
            </h2>
            <p className="text-sm text-secondary-500">{user.email}</p>
            <p className="text-xs text-secondary-400 mt-1">
              {isCandidate ? 'Соискатель' : isEmployer ? 'Работодатель' : 'Администратор'}
            </p>

            {user.last_login_at && (
              <p className="text-xs text-secondary-400 mt-2">
                Последний вход: {formatDate(user.last_login_at)}
              </p>
            )}
          </Card>
        </div>

        {/* Правая колонка - вкладки */}
        <div className="lg:col-span-3">
          <Card className="p-6">
            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tab value="profile">Профиль</Tab>
              <Tab value="security">Безопасность</Tab>
              {isCandidate && <Tab value="resumes">Резюме</Tab>}
              {isEmployer && <Tab value="companies">Компании</Tab>}
              {(isEmployer || isCandidate) && <Tab value="subscription">Тариф</Tab>}
              <Tab value="notifications">Уведомления</Tab>
              <Tab value="stats">Статистика</Tab>
            </Tabs>

            <TabPanel value="subscription">
              <div className="mt-4 space-y-4">
                <Card className="p-4"><h2 className="font-semibold">Текущий доступ</h2><p className="mt-1">{access.data?.plan_name || 'Загрузка…'}{access.data?.ends_at ? ` · до ${new Date(access.data.ends_at).toLocaleDateString('ru-RU')}` : ' · бессрочно'}</p><p className="mt-2 text-sm text-secondary-600">Лимиты: {access.data ? Object.entries(access.data).filter(([key]) => !['plan_code','plan_name','ends_at','subscription_id'].includes(key)).map(([key,value])=>`${key}: ${value}`).join(' · ') : ''}</p></Card>
                <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="font-semibold">Доступные тарифы</h2><Link to="/subscriptions" className="text-sm font-semibold text-primary-700 hover:underline">Все условия и способы оплаты →</Link></div>
                <div className="grid gap-3 md:grid-cols-2">{(plans.data?.plans || []).filter((plan:any)=>plan.audience === user?.role).map((plan:any)=><Card className="p-4" key={plan.id}><h3 className="font-semibold">{plan.name}</h3><p className="my-2 text-sm text-secondary-600">{plan.price_rub > 0 ? `${plan.price_rub.toLocaleString('ru-RU')} ₽ / ${plan.duration_days} дней` : 'Бесплатно'}</p><p className="text-sm leading-6 text-secondary-600">{plan.description}</p><ul className="mt-3 space-y-1 text-xs text-secondary-600">{(plan.features || []).map((feature:string)=><li key={feature}>✓ {feature}</li>)}</ul><p className="mt-3 text-xs text-secondary-500">{plan.terms}</p>{plan.price_rub > 0 && <Link to={`/subscriptions?plan=${plan.id}`} className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700">Купить</Link>}</Card>)}</div>
                {!plans.data?.online_payment_available && <p className="text-xs text-secondary-500">Онлайн-оплата пока настраивается. Заявку на подключение тарифа можно оставить на странице подписок.</p>}
              </div>
            </TabPanel>

            {/* Вкладка профиля */}
            <TabPanel value="profile">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Имя *"
                    placeholder="Иван"
                    error={errors.first_name?.message}
                    {...register('first_name')}
                  />
                  <Input
                    label="Фамилия *"
                    placeholder="Иванов"
                    error={errors.last_name?.message}
                    {...register('last_name')}
                  />
                  <Input
                    label="Телефон"
                    placeholder="+7 999 123-45-67"
                    error={errors.phone?.message}
                    {...register('phone')}
                  />
                  <Select
                    label="Язык"
                    options={[
                      { value: 'ru', label: 'Русский' },
                      { value: 'en', label: 'English' },
                      { value: 'kz', label: 'Қазақша' },
                      { value: 'de', label: 'Deutsch' },
                      { value: 'fr', label: 'Français' },
                    ]}
                    error={errors.language?.message}
                    {...register('language')}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    isLoading={isSubmitting || updateProfileMutation.isPending}
                  >
                    Сохранить изменения
                  </Button>
                </div>
              </form>
            </TabPanel>

            {/* Вкладка безопасности */}
            <TabPanel value="security">
              <div className="mt-4">
                <h3 className="font-semibold text-secondary-900 mb-4">Смена пароля</h3>
                <Button onClick={passwordModal.open}>
                  Изменить пароль
                </Button>
              </div>
            </TabPanel>

            <TabPanel value="notifications">
              <TelegramSettings />
            </TabPanel>

            {/* Вкладка резюме */}
            {isCandidate && (
              <TabPanel value="resumes">
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-secondary-900">Мои резюме</h3>
                    <Button size="sm" onClick={() => navigate('/resumes/create')}>
                      + Создать
                    </Button>
                  </div>
                  {resumesLoading ? (
                    <div className="flex justify-center py-8">
                      <Spinner size="md" />
                    </div>
                  ) : (
                    <ResumeList
                      resumes={resumesData?.items || []}
                      showScore={false}
                      onEdit={(resume) => navigate(`/resumes/${resume.id}/edit`)}
                    />
                  )}
                  <Button
                    variant="ghost"
                    className="mt-4"
                    onClick={() => navigate('/resumes')}
                  >
                    Все резюме →
                  </Button>
                </div>
              </TabPanel>
            )}

            {/* Вкладка компаний */}
            {isEmployer && (
              <TabPanel value="companies">
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-secondary-900">Мои компании</h3>
                    <Button size="sm" onClick={() => navigate('/companies/create')}>
                      + Создать
                    </Button>
                  </div>
                  {companiesLoading ? (
                    <div className="flex justify-center py-8">
                      <Spinner size="md" />
                    </div>
                  ) : (
                    <CompanyList
                      showCreate={false}
                      onCompanyClick={(company) => navigate(`/companies/${company.id}`)}
                    />
                  )}
                  <Button
                    variant="ghost"
                    className="mt-4"
                    onClick={() => navigate('/companies')}
                  >
                    Все компании →
                  </Button>
                </div>
              </TabPanel>
            )}

            {/* Вкладка статистики */}
            <TabPanel value="stats">
              <div className="mt-4">
                {statsLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : stats ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {isCandidate && (
                      <>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.resumes_count || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Резюме</p>
                        </Card>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.applications_count || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Отклики</p>
                        </Card>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.resume_views || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Просмотры</p>
                        </Card>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.invites_count || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Приглашения</p>
                        </Card>
                      </>
                    )}
                    {isEmployer && (
                      <>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.total_vacancies || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Вакансии</p>
                        </Card>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.active_vacancies || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Активные</p>
                        </Card>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.total_applications || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Отклики</p>
                        </Card>
                        <Card className="p-4 text-center">
                          <p className="text-2xl font-bold text-primary-600">
                            {stats.new_applications || 0}
                          </p>
                          <p className="text-xs text-secondary-500">Новые</p>
                        </Card>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-secondary-500 text-center py-8">
                    Статистика недоступна
                  </p>
                )}
              </div>
            </TabPanel>
          </Card>
        </div>
      </div>

      {/* Модалка смены пароля */}
      <Modal
        isOpen={passwordModal.isOpen}
        onClose={passwordModal.close}
        title="Смена пароля"
      >
        <form onSubmit={handlePasswordChange} className="space-y-4 py-4">
          <Input
            label="Текущий пароль"
            type="password"
            name="old_password"
            placeholder="••••••••"
            required
          />
          <Input
            label="Новый пароль"
            type="password"
            name="new_password"
            placeholder="••••••••"
            required
          />
          <Input
            label="Подтверждение пароля"
            type="password"
            name="confirm_password"
            placeholder="••••••••"
            required
          />

          <div className="flex justify-end gap-3 border-t border-secondary-200 pt-4">
            <Button variant="ghost" type="button" onClick={passwordModal.close}>
              Отмена
            </Button>
            <Button
              type="submit"
              isLoading={changePasswordMutation.isPending}
            >
              Сменить пароль
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

export default Profile
